import {
  EXTENSION_AUTH_MESSAGES,
  OLIO_EXTENSION_HANDOFF_PORT,
  OLIO_EXTENSION_ID,
} from 'components/inboundReferrals/constants';
import http from 'services/http';

// Hands the browser extension an API token minted from the signed-in Olio web session,
// over Chrome's externally_connectable channel (targeted to our extension ID).

interface ExtensionTokenResponse {
  accessToken: string;
  expiresAt: number;
  user: { email: string };
}

// chrome.runtime is injected only when the extension is installed and this origin is in
// its externally_connectable matches; absent in other browsers or with no extension.
function externalMessaging(): { sendMessage: (id: string, msg: unknown, cb: () => void) => void } | undefined {
  const runtime = (window as any).chrome?.runtime;
  return runtime?.sendMessage ? runtime : undefined;
}

// Whether the extension can be reached from this page at all.
export function isExtensionReachable(): boolean {
  return !!externalMessaging();
}

// A long-lived port to the extension. The extension posts renewal requests over it; because
// only this SPA can open an externally_connectable port to the pinned ID, those requests are
// authenticated (a page script can't forge them the way it could a window message).
export interface ExtensionPort {
  postMessage: (msg: unknown) => void;
  onMessage: { addListener: (cb: (msg: { type?: string }) => void) => void };
  onDisconnect: { addListener: (cb: () => void) => void };
  disconnect: () => void;
}

export function connectToExtension(): ExtensionPort | undefined {
  const runtime = (window as any).chrome?.runtime;
  if (!runtime?.connect) return undefined;
  try {
    return runtime.connect(OLIO_EXTENSION_ID, { name: OLIO_EXTENSION_HANDOFF_PORT });
  } catch {
    return undefined;
  }
}

// Read (and thereby clear) any pending runtime.lastError so a port disconnect — routine
// when the MV3 service worker idles out — doesn't log an "Unchecked runtime.lastError".
export function clearRuntimeError(): void {
  void (window as any).chrome?.runtime?.lastError;
}

async function mintExtensionToken(): Promise<ExtensionTokenResponse> {
  const { data } = await http.post<ExtensionTokenResponse>('/extension_token');
  return data;
}

// Mint a token and deliver it to the extension. No-ops (without minting) when the
// extension isn't reachable on this page.
export async function handOffTokenToExtension(): Promise<boolean> {
  const runtime = externalMessaging();
  if (!runtime) return false;

  const token = await mintExtensionToken();

  return new Promise((resolve) => {
    runtime.sendMessage(OLIO_EXTENSION_ID, { type: EXTENSION_AUTH_MESSAGES.handoff, ...token }, () => {
      // Reading lastError suppresses the "unchecked runtime.lastError" console warning.
      void (window as any).chrome?.runtime?.lastError;
      resolve(true);
    });
  });
}
